import { makeAutoObservable, runInAction } from "mobx";
import { Category, HomeShowItem, SetHomeShowRequest, CategoryTreeItem } from "../../models/CategoryManagement";
import { CategoryApi } from "../../apis/Category";

export class CategoryManagementPageModel {
    categories: Category[] = [];
    categoryTree: CategoryTreeItem[] = [];
    homeShows: HomeShowItem[] = [];
    loading: boolean = false;
    editingCategory: Category | null = null;
    showEditModal: boolean = false;
    showHomeShowModal: boolean = false;
    showAddCategoryModal: boolean = false;
    selectedSecondCategoryId: number = 0;
    selectedIndex: number = 0;
    newCategoryForm: Partial<Category> = {};

    constructor() {
        makeAutoObservable(this);
        this.newCategoryForm = { parentId: 0 };
    }

    async loadCategories() {
        runInAction(() => { this.loading = true; });
        try {
            const categories = await CategoryApi.getAllCategories();
            runInAction(() => {
                this.categories = categories;
                this.buildCategoryTree();
            });
        } finally {
            runInAction(() => { this.loading = false; });
        }
    }

    async loadHomeShows() {
        runInAction(() => { this.loading = true; });
        try {
            const homeShows = await CategoryApi.getHomeShows();
            runInAction(() => { this.homeShows = homeShows; });
        } finally {
            runInAction(() => { this.loading = false; });
        }
    }

    buildCategoryTree() {
        const firstLevel = this.categories.filter(cat => cat.parentId === 0);
        this.categoryTree = firstLevel.map(cat => ({
            ...cat,
            children: this.categories.filter(child => child.parentId === cat.id)
        }));
    }

    setEditingCategory(category: Category | null) {
        this.editingCategory = category;
        this.showEditModal = !!category;
    }

    setShowHomeShowModal(show: boolean) {
        this.showHomeShowModal = show;
        if (!show) {
            this.selectedSecondCategoryId = 0;
            this.selectedIndex = 0;
        }
    }

    setShowAddCategoryModal(show: boolean) {
        this.showAddCategoryModal = show;
        if (!show) {
            this.newCategoryForm = { parentId: 0 };
        } else {
            this.newCategoryForm = { parentId: 0 };
        }
    }

    setSelectedSecondCategoryId(id: number) {
        this.selectedSecondCategoryId = id;
    }

    setSelectedIndex(index: number) {
        this.selectedIndex = index;
    }

    setNewCategoryForm(form: Partial<Category>) {
        this.newCategoryForm = { ...form, parentId: form.parentId || 0 };
    }

    async addCategory(params: Partial<Category>) {
        runInAction(() => { this.loading = true; });
        try {
            const success = await CategoryApi.addCategory(params);
            if (success) {
                await this.loadCategories();
                this.setShowAddCategoryModal(false);
            }
        } finally {
            runInAction(() => { this.loading = false; });
        }
    }

    async hideMainCategory(categoryId: number) {
        runInAction(() => { this.loading = true; });
        try {
            const success = await CategoryApi.hideMainCategory(categoryId);
            if (success) {
                await this.loadCategories();
            }
        } finally {
            runInAction(() => { this.loading = false; });
        }
    }

    async deleteMainCategory(categoryId: number) {
        runInAction(() => { this.loading = true; });
        try {
            const success = await CategoryApi.deleteMainCategory(categoryId);
            if (success) {
                await this.loadCategories();
                await this.loadHomeShows();
            }
            return success;
        } finally {
            runInAction(() => { this.loading = false; });
        }
    }

    async deleteSecondCategory(categoryId: number) {
        runInAction(() => { this.loading = true; });
        try {
            const success = await CategoryApi.deleteSecondCategory(categoryId);
            if (success) {
                await this.loadCategories();
            }
        } finally {
            runInAction(() => { this.loading = false; });
        }
    }

    async updateCategory(updates: Partial<Category>) {
        if (!this.editingCategory) return;
        
        runInAction(() => { this.loading = true; });
        try {
            const success = await CategoryApi.updateCategory(updates);
            if (success) {
                await this.loadCategories();
                this.setEditingCategory(null);
            }
        } finally {
            runInAction(() => { this.loading = false; });
        }
    }

    async setHomeShow() {
        if (this.selectedSecondCategoryId === 0) return;
        
        runInAction(() => { this.loading = true; });
        try {
            const request: SetHomeShowRequest = {
                index: this.selectedIndex,
                secondCategoryId: this.selectedSecondCategoryId
            };
            const success = await CategoryApi.setHomeShow(request);
            if (success) {
                await this.loadHomeShows();
                this.setShowHomeShowModal(false);
            }
        } finally {
            runInAction(() => { this.loading = false; });
        }
    }

    getSecondCategories(): Category[] {
        return this.categories.filter(cat => cat.parentId !== 0);
    }
}
